import { useState } from "react";
import { ResponsiveLine } from "@nivo/line";
import type { DatumId } from "@nivo/pie";
import { format } from "date-fns";
import { t } from "@/i18n-js/instance";
import { MembersTooltip } from "@/react/components/SettingsApp/AnalyticsV4/components/tooltips/MembersTooltip";
import { abbreviateNumber } from "@/react/helpers/number";
import { CHART_TYPES } from "@/react/hooks/analytics/chartTypes";
import { useLineChartComputed } from "@/react/hooks/analytics/useLineChartComputed";
import { parseDayIdToLocalDate } from "@/react/utils/dates/dayId";
import { useThemeContext } from "@circle-react/providers";
import { ChartContainer } from "../../shared/ChartContainer";
import { EmptyState } from "../../shared/EmptyState";
import { LegendComponent } from "../../shared/LegendComponent";
import { LoadingChartComponent } from "../../shared/LoadingChartComponent";
import { useContainerSize } from "../../shared/hooks/useContainerSize";
import { DATE_FORMAT } from "../constants";
import { buildRightAxis, buildYScale } from "./axisBuilders";
import { buildHoverDotLayer } from "./buildHoverDotLayer";
import { buildLineTooltip } from "./buildLineTooltip";
import { buildPreviousPeriodLayer } from "./buildPreviousPeriodLayer";
import { baseAxisProps, lineChartTheme } from "./constants";
import { mapPrevSeriesToCurrentDomain } from "./mapPrevSeriesToCurrentDomain";
import type { LineChartComponentProps } from "./types";

export const LineChartComponent = ({
  isLoading,
  className,
  title,
  data,
  previousData,
  tooltip,
  titleLabel,
  indexBy,
  seriesKeys,
  colors,
  dateGrouping,
  curve = "monotoneX",
  enableArea = true,
  enableGridX = false,
  enableGridY = true,
  emptyStateTitle,
  emptyStateDescription,
  customTooltip,
  rootLocale,
  scaleType = "linear",
  showLegend = false,
  secondaryAxis,
  translateLegendLabels = true,
}: LineChartComponentProps) => {
  const { containerWidth, containerHeight, containerRef } = useContainerSize();
  const [activeId, setActiveId] = useState<DatumId | null>(null);

  const { isLightAppearance } = useThemeContext();
  const [hoverPoint, setHoverPoint] = useState<{
    x: number;
    y: number;
    color: string;
  } | null>(null);
  const marginTop = 32;
  const marginBottom = 50;

  const {
    seriesColorById,
    gradientDefs,
    fillPattern,
    tickValues,
    formattedData,
    yTicks,
    yMinScale,
    yMaxScale,
    yMaxForNormalize,
    legendData,
    isSingleDataPoint,
    normalizedHeight,
  } = useLineChartComputed({
    data,
    seriesKeys,
    indexBy,
    colors,
    activeId,
    containerWidth,
    containerHeight,
    secondaryAxis,
    marginTop,
    marginBottom,
  });

  if (isLoading) {
    return <LoadingChartComponent type="line" className={className} />;
  }

  if (!data.length) {
    return (
      <EmptyState
        chartTitle={title}
        tooltip={tooltip}
        className={className}
        emptyTitle={emptyStateTitle}
        emptyDescription={emptyStateDescription}
      />
    );
  }

  const theme = lineChartTheme(Boolean(isLightAppearance));
  const currentXDomain: Array<string | number> =
    formattedData?.[0]?.data?.map(d => d.x) || [];
  const { prevValuesOrdered, prevPoints } = mapPrevSeriesToCurrentDomain(
    previousData,
    seriesKeys[0],
    currentXDomain,
    indexBy,
  );
  const previousPeriodLayer = buildPreviousPeriodLayer({
    prevPoints,
    seriesKeys,
    seriesColorById,
    curveName: curve,
  });
  const hoverDotLayerFn = buildHoverDotLayer(hoverPoint);
  const tooltipFn = buildLineTooltip({
    previousData,
    seriesKeys,
    currentXDomain,
    prevValuesOrdered,
    dateGrouping,
    renderMembersTooltip: ({
      xValue,
      currentValue,
      headerLabel,
      deltaPercent,
    }) => (
      <MembersTooltip
        data={{ Date: xValue, total_members: currentValue }}
        rootLocale={rootLocale.join(".")}
        headerLabel={headerLabel}
        trendDeltaPercentage={deltaPercent}
      />
    ),
    customTooltip,
  });

  return (
    <ChartContainer
      title={title}
      tooltip={tooltip}
      titleLabel={titleLabel}
      className={className}
      data={data}
      chartType={CHART_TYPES.LINE}
    >
      <div className="flex flex-col gap-4">
        {showLegend && (
          <LegendComponent
            data={legendData}
            orientation="horizontal"
            setActiveId={setActiveId}
            className="self-start"
            rootLocale={translateLegendLabels ? rootLocale : undefined}
          />
        )}
        <div
          ref={containerRef}
          className="relative flex h-80 w-full overflow-visible"
          style={containerHeight ? { height: normalizedHeight } : undefined}
        >
          <ResponsiveLine
            data={formattedData}
            margin={{
              top: marginTop,
              right:
                (secondaryAxis &&
                  secondaryAxis.keys &&
                  secondaryAxis.keys.length) ||
                0
                  ? 80
                  : 50,
              bottom: marginBottom,
              left: 50,
            }}
            xScale={{ type: "point" }}
            yScale={buildYScale(
              Boolean(
                (secondaryAxis &&
                  secondaryAxis.keys &&
                  secondaryAxis.keys.length) ||
                  0,
              ),
              yMaxForNormalize,
              scaleType,
              yMinScale,
              yMaxScale,
            )}
            curve={curve}
            axisTop={null}
            axisRight={buildRightAxis(
              Boolean(
                (secondaryAxis &&
                  secondaryAxis.keys &&
                  secondaryAxis.keys.length) ||
                  0,
              ),
              yMaxForNormalize,
              secondaryAxis?.format,
              rootLocale,
            )}
            axisBottom={{
              ...baseAxisProps,
              tickSize: 0,
              legendOffset: 40,
              tickValues: tickValues,
              format: (value: string | number | Date) =>
                format(parseDayIdToLocalDate(String(value)), DATE_FORMAT),
            }}
            axisLeft={{
              ...baseAxisProps,
              legendOffset: -45,
              legendPosition: "middle",
              tickSize: 0,
              tickValues: yTicks,
              format: abbreviateNumber,
            }}
            enableGridX={enableGridX}
            enableGridY={enableGridY}
            gridYValues={yTicks}
            colors={seriesKeys.map(k => seriesColorById[k])}
            // Always enable points so we can show a hover dot; hide them visually unless single point
            enablePoints
            pointSize={isSingleDataPoint ? 8 : 0}
            pointLabelYOffset={-12}
            enableArea={enableArea}
            defs={gradientDefs}
            fill={fillPattern}
            useMesh
            onMouseMove={(point: {
              x: number;
              y: number;
              serieColor?: string;
              seriesId?: DatumId;
              serieId?: DatumId;
              color?: string;
            }) => {
              const seriesId = point.seriesId ?? point.serieId;
              const colorFromMap =
                seriesId !== undefined
                  ? seriesColorById[String(seriesId)]
                  : undefined;
              const color =
                point.serieColor ?? point.color ?? colorFromMap ?? "";
              setHoverPoint({ x: point.x, y: point.y, color });
              if (seriesId !== undefined) setActiveId(seriesId);
            }}
            onMouseLeave={() => {
              setActiveId(null);
              setHoverPoint(null);
            }}
            tooltip={tooltipFn}
            theme={theme}
            ariaLabel={t("settings.analytics.line_chart")}
            layers={[
              "grid",
              "axes",
              "areas",
              "lines",
              previousPeriodLayer,
              hoverDotLayerFn,
              "points",
              "mesh",
            ]}
          />
        </div>
      </div>
    </ChartContainer>
  );
};

LineChartComponent.displayName = "LineChartComponent";
